import { HttpClient } from '@angular/common/http';
import { Injectable, OnInit } from '@angular/core';
import { Observable } from 'rxjs';
import { environment } from 'src/environments/environment';

import { MatDialog } from '@angular/material/dialog';
import { MatTreeNestedDataSource } from '@angular/material/tree';
import { Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { NavListCommonProps } from 'src/app/components/nav/nav.component';
import { DialogSelectSolutionComponent } from 'src/app/components/shared/dialog-select-solution/dialog-select-solution.component';
import { DialogImportSeveFileComponent } from '../../components/shared/dialog-import-seve-file/dialog-import-seve-file.component';
import { FunctionalityCode } from '../_enums/functionality.code';
import { SiteMapUrl } from '../_enums/sitemap.url';
import { SnackbarTypes } from '../_enums/snackbar.types';
import { Project } from '../_models/project';
import { ImportWorkInspectionResponse, WorkInspectionModel } from '../_models/workInspection';
import { AuthService } from './auth.service';
import { SnackbarService } from './snackbar.service';
import { SolutionService } from './solution.service';
import { SpinnerOverlayService } from './spinner.overlay.service';

@Injectable({
  providedIn: 'root'
})
export class WorkInspectionService {
  private apiUrl = `${environment.apiUrl}/WorkInspection`;
  private updateDataSourceCallback?: (newDataSource: any[]) => void;

  constructor(
    private http: HttpClient,
    private authService: AuthService,
    private snackbarService: SnackbarService,
    private matDialog: MatDialog,
    private solutionService: SolutionService,
    private router: Router,
    private translate: TranslateService,
    private spinnerService: SpinnerOverlayService
  ) {}

  getAll(): Observable<WorkInspectionModel[]> {
    return this.http.get<WorkInspectionModel[]>(`${this.apiUrl}/list`, { headers: this.authService.headers });
  }

  getById(id: string): Observable<WorkInspectionModel> {
    return this.http.get<WorkInspectionModel>(`${this.apiUrl}/${id}`, { headers: this.authService.headers });
  }

  createFromSolution(solutionId: string): Observable<{ id: string }> {
    const payload = { solId: solutionId };
    return this.http.post<{ id: string }>(`${this.apiUrl}/createFromSolution`, payload, { headers: this.authService.headers });
  }

  delete(id: string): Observable<void> {
    return this.http.delete<void>(`${this.apiUrl}/${id}`, { headers: this.authService.headers });
  }

  deleteMany(resourceIds: string[]) {
    return this.http.post<{ results: { itemId?: string; resourceId?: string; id?: string; success: boolean }[] }>(
      `${this.apiUrl}/deleteBatch`,
      {
        ItemIds: resourceIds
      },
      {
        headers: this.authService.headers
      }
    );
  }

  update(workInspection: WorkInspectionModel): Observable<WorkInspectionModel> {
    return this.http.put<WorkInspectionModel>(`${this.apiUrl}/${workInspection.workInspectionId}`, workInspection, { headers: this.authService.headers });
  }

  exportWorkInspectionExcel(wiId: string): Observable<Blob> {
    const payload = { wiId: wiId };
    return this.http.post(`${this.apiUrl}/exportExcel`, payload, {
      headers: this.authService.headers,
      responseType: 'blob'
    });
  }

  exportWorkInspectionFile(wiId: string): Observable<Blob> {
    const payload = { wiId: wiId };
    return this.http.post(`${this.apiUrl}/export`, payload, {
      headers: this.authService.headers,
      responseType: 'blob'
    });
  }

  importWorkInspectionFile(formData: FormData): Observable<ImportWorkInspectionResponse> {
    return this.http.post<ImportWorkInspectionResponse>(`${this.apiUrl}/import`, formData, { headers: this.authService.headers });
  }

  createWorkInspection(perimeterId: string, updateDataSource: (newDataSource: any[]) => void): void {
    const dialogRef = this.matDialog.open(DialogSelectSolutionComponent, {
      width: '1100px',
      maxWidth: '95vw',
      data: { perimeterId: perimeterId, selectedSolution: null }
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result && result.id) {
        this.spinnerService.show();
        this.createFromSolution(result.id).subscribe({
          next: (res) => {
            this.snackbarService.open(SnackbarTypes.add);
            this.getAll().subscribe({
              next: (data) => {
                this.spinnerService.hide();
                updateDataSource(data);
                this.router.navigate([`${SiteMapUrl.MenuWorkInspection}/${res.id}`], {
                  state: {
                    projectId: res.id,
                    perimeterId: perimeterId
                  }
                });
              },
              error: () => this.spinnerService.hide()
            });
          },
          error: (err) => {
            this.spinnerService.hide();
            this.snackbarService.open('error_creating_work_inspection');
          }
        });
      }
    });
  }

  onImportProject(perimeterId: string, updateDataSource: (newDataSource: any[]) => void) {
    const importDialogRef = this.matDialog.open(DialogImportSeveFileComponent, {
      data: { perimeterId }
    });

    importDialogRef.afterClosed().subscribe((result) => {
      if (result && result.success && result.file) {
        const formData = new FormData();
        formData.append('file', result.file);
        this.importWorkInspectionFile(formData).subscribe(
          (importResult) => {
            result.stopLoading?.();
            if (importResult.isSuccessful) {
              this.snackbarService.open(SnackbarTypes.add);
              this.getAll();
              setTimeout(() => {
                this.router.navigate([`${SiteMapUrl.MenuWorkInspection}/${importResult.importedWorkInspectionId}`]);
              }, 500);
            } else {
              this.snackbarService.open(SnackbarTypes.error);
              if (importResult.error) this.snackbarService.showMessage(importResult.error);
            }
          },
          (error: any) => {
            result.stopLoading?.();
            console.error('Import error:', error);
            this.snackbarService.open(SnackbarTypes.error);
          }
        );
      }
    });
  }

  refreshDataSource(): void {
    if (this.updateDataSourceCallback) {
      this.getAll().subscribe((data) => {
        this.updateDataSourceCallback!(data);
      });
    }
  }

  getNavConfig(commonProps: NavListCommonProps) {
    this.updateDataSourceCallback = commonProps.updateDataSource;

    const userAccesses = this.authService.accessesValues || [];
    const canManage = userAccesses.includes(FunctionalityCode.ManageWorkInspection);
    const canImport = userAccesses.includes(FunctionalityCode.ImportWorkInspection);

    const actions = [];
    if (canManage) {
      actions.push({
        label: 'action_create_work_inspection',
        onClick: () => this.createWorkInspection(commonProps.perimeterId, commonProps.updateDataSource)
      });
    }
    if (canImport) {
      actions.push({
        label: 'action_import_work_inspection',
        onClick: () => this.onImportProject(commonProps.perimeterId, commonProps.updateDataSource)
      });
    }

    return {
      header: {
        title: 'work_inspection_header_title',
        description: 'work_inspection_header_description',
        actions: actions,
        filters: []
      },
      getDataSource: this.getAll.bind(this),
      getItemConfig: (node: any) => {
        return {
          displayName: node.code ? `${node.displayName} [${node.code}]` : node.displayName,
          url: `${SiteMapUrl.MenuWorkInspection}/${node.workInspectionId}`,
          itemType: 'work-inspection-item',
          state: {
            projectId: node.workInspectionId,
            perimeterId: commonProps.perimeterId
          }
        };
      },
      onDelete: (selectedItems: any[]) => this.onDeleteWorkInspections(selectedItems, commonProps.dataSource, commonProps.updateDataSource, commonProps.navComponent)
    };
  }

  private onDeleteWorkInspections(selectedItems: any[], dataSource: MatTreeNestedDataSource<any>, updateDataSource: (newDataSource: any[]) => void, navComponent: any) {
    return navComponent.createDeleteHandler({
      deleteMany: (ids: string[]) => this.deleteMany(ids),
      getItemId: (item: any) => item?.workInspectionId || item?.id || item?.key,
      filterData: (data: any[], deletedIds: string[]) => this.filterNodes(data, deletedIds),
      successMessage: (count: number) => this.translate.instant(count > 1 ? 'success_resources_deleted' : 'success_resource_deleted'),
      errorMessage: 'error_no_item_selected',
      snackbarService: this.snackbarService
    })(selectedItems, dataSource, updateDataSource);
  }

  private filterNodes(nodes: any[], ids: string[]): any[] {
    if (!Array.isArray(nodes) || !nodes.length) {
      return [];
    }
    const filtered: any[] = [];
    nodes.forEach((node) => {
      if (!node) {
        return;
      }
      const nodeId = node?.workInspectionId || node?.id || node?.key;
      if (nodeId && ids.includes(nodeId)) {
        return;
      }
      if (Array.isArray(node.value)) {
        const nextChildren = this.filterNodes(node.value, ids);
        if (!nextChildren.length && !nodeId) {
          return;
        }
        filtered.push({ ...node, value: nextChildren });
        return;
      }
      filtered.push(node);
    });
    return filtered;
  }
}
